import axios from 'axios'
import i18n from '@/translations'
import router from '@/router'
import store from '@/store'
import localStorageUtil from '@/utils/localStorageUtil'
import { ROUTE_LOGIN } from '@/const/routeNames'

const musicboxClient = axios.create()

const responseCache = {
  'playlists': null
}

musicboxClient.interceptors.request.use(
  (config) => {
    const token = localStorageUtil.getToken()
    if (token) {
      config.headers['Authorization'] = 'Token ' + token
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

musicboxClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response == null) {
      store.commit('setNotification', { message: i18n.t('Can\'t get response from server'), timeout: 5000, type: 'danger' })
    } else if (error.response.status === 401) {
      localStorageUtil.removeToken()
      store.commit('setAuthorized', false)
      router.push(ROUTE_LOGIN)
    }
    return Promise.reject(error)
  }
)

const ROOT = '/api/v1'

musicboxClient.loginRequest = (data) => musicboxClient.post(ROOT + '/account/login', data)
musicboxClient.getAccountDetails = () => musicboxClient.get(ROOT + '/account/details')

musicboxClient.getNews = (boxUuid) => musicboxClient.get(ROOT + '/news/')
musicboxClient.getBoxes = (boxUuid) => musicboxClient.get(ROOT + '/boxes/')
musicboxClient.getBoxInfo = (boxUuid) => musicboxClient.get(ROOT + '/boxes/' + boxUuid + '/')
musicboxClient.getBoxStatus = (boxUuid) => musicboxClient.get(ROOT + '/boxes/' + boxUuid + '/status')
musicboxClient.stopBox = (boxUuid) => musicboxClient.post(ROOT + '/boxes/' + boxUuid + '/stop')
musicboxClient.addSongToQueue = (boxUuid, data) => musicboxClient.post(ROOT + '/boxes/' + boxUuid + '/queue/', data)
musicboxClient.removeSongFromQueue = (boxUuid, songId) => musicboxClient.delete(ROOT + '/boxes/' + boxUuid + '/queue/' + songId)
musicboxClient.reorderQueue = (boxUuid, songIds) => musicboxClient.put(ROOT + '/boxes/' + boxUuid + '/queue/', { song_ids: songIds })
musicboxClient.skipCurrentSong = (boxUuid) => musicboxClient.post(ROOT + '/boxes/' + boxUuid + '/skip')
musicboxClient.boxAllowCurrentPlaylist = (boxUuid, jukeboxAllowCurrent) => musicboxClient.put(
  ROOT + '/boxes/' + boxUuid + '/',
  { uuid: boxUuid, jukebox_allow_current: jukeboxAllowCurrent }
)
musicboxClient.boxAllowPlaylists = (boxUuid, playlistIds) => musicboxClient.post(
  ROOT + '/boxes/' + boxUuid + '/jukebox/lists/',
  { uuid: boxUuid, playlist_ids: playlistIds }
)
musicboxClient.getJukeboxCode = (boxUuid) => musicboxClient.post(ROOT + '/boxes/' + boxUuid + '/jukebox/')
musicboxClient.setOfflineList = (boxUuid, offlineListId) => musicboxClient.post(
  ROOT + '/boxes/' + boxUuid + '/set_offline_list/',
  { uuid: boxUuid, offline_list_id: offlineListId }
)
musicboxClient.setBoxPlaylist = (boxUuid, listId) => musicboxClient.post(ROOT + '/boxes/' + boxUuid + '/switch', { list_id: listId })

musicboxClient.getSchedules = (boxUuid) => musicboxClient.get(ROOT + '/boxes/' + boxUuid + '/schedule/playlists/')
musicboxClient.createSchedule = (boxUuid, data) => musicboxClient.post(ROOT + '/boxes/' + boxUuid + '/schedule/playlists/', data)
musicboxClient.getSchedule = (boxUuid, scheduleId) => musicboxClient.get(ROOT + '/boxes/' + boxUuid + '/schedule/playlists/' + scheduleId)
musicboxClient.deleteSchedule = (boxUuid, scheduleId) => musicboxClient.delete(ROOT + '/boxes/' + boxUuid + '/schedule/playlists/' + scheduleId)
musicboxClient.updateSchedule = (boxUuid, scheduleId, data) => musicboxClient.put(ROOT + '/boxes/' + boxUuid + '/schedule/playlists/' + scheduleId, data)

musicboxClient.searchSongs = (params) => musicboxClient.get(ROOT + '/songs/', { params })

musicboxClient.getJingles = () => musicboxClient.get(ROOT + '/jingles/')

musicboxClient.uploadJingle = (file, title) => {
  return musicboxClient.post(
    ROOT + '/jingles/' + (title ? '?title=' + encodeURIComponent(title) : ''),
    file,
    {
      headers: { 'Content-Type': 'application/octet-stream' },
      transformRequest: [(data) => data]
    }
  )
}

musicboxClient.deleteJingle = (jingleId) => musicboxClient.delete(ROOT + '/jingles/' + jingleId)

musicboxClient.getJingleBeds = () => musicboxClient.get(ROOT + '/jingles/beds/')

musicboxClient.orderJingle = (text) => musicboxClient.post(ROOT + '/jingles/order/', { text })

musicboxClient.getJingleSchedules = (boxUuid) => musicboxClient.get(ROOT + '/boxes/' + boxUuid + '/schedule/jingles/')
musicboxClient.getJingleBlock = (boxUuid, scheduleId) => musicboxClient.get(ROOT + '/boxes/' + boxUuid + '/schedule/jingles/' + scheduleId)
musicboxClient.createJingleSchedule = (boxUuid, data) => musicboxClient.post(ROOT + '/boxes/' + boxUuid + '/schedule/jingles/', data)
musicboxClient.deleteJingleSchedule = (boxUuid, scheduleId) => musicboxClient.delete(ROOT + '/boxes/' + boxUuid + '/schedule/jingles/' + scheduleId)
musicboxClient.updateJingleSchedule = (boxUuid, scheduleId, data) => musicboxClient.put(ROOT + '/boxes/' + boxUuid + '/schedule/jingles/' + scheduleId, data)

musicboxClient.getPlaylists = () => {
  if (responseCache.playlists && new Date().getTime() - responseCache.playlists.created < 30000) {
    return new Promise(resolve => { setTimeout(() => { resolve(responseCache.playlists.response) }, 100) })
  }
  return new Promise((resolve, reject) => {
    musicboxClient
      .get(ROOT + '/playlists/')
      .then(res => {
        responseCache.playlists = {
          response: res,
          created: new Date().getTime()
        }
        resolve(res)
      })
      .catch(e => {
        reject(e)
      })
  })
}

musicboxClient.getPlaylist = (id) => musicboxClient.get(ROOT + '/playlists/' + id)
musicboxClient.createPlaylist = (newPlaylist) => musicboxClient.post(ROOT + '/playlists/', newPlaylist)
musicboxClient.updatePlaylist = (playlistId, updatedPlaylist) => musicboxClient.put(ROOT + '/playlists/' + playlistId, updatedPlaylist)
musicboxClient.deletePlaylist = (playlistId) => musicboxClient.delete(ROOT + '/playlists/' + playlistId)

export default musicboxClient



// WEBPACK FOOTER //
// ./src/utils/musicboxClient.js